import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Space,
  Tag,
  Popconfirm,
  Typography,
  Descriptions,
  Alert,
  Radio,
  Select,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  DeleteOutlined,
  StopOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DisconnectOutlined,
  ApiOutlined,
  InfoCircleOutlined,
  SwapOutlined,
  MessageOutlined,
  ClearOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Tooltip } from 'antd';
import { botAPI } from '../services/api';
import { BotConfig, BotType } from '../types';

const { TextArea } = Input;
const { Title } = Typography;

const BotsManagement: React.FC = () => {
  const navigate = useNavigate();
  const [bots, setBots] = useState<BotConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [webhookModalVisible, setWebhookModalVisible] = useState(false);
  const [webhookInfoModalVisible, setWebhookInfoModalVisible] = useState(false);
  const [switchModeModalVisible, setSwitchModeModalVisible] = useState(false);
  const [currentBot, setCurrentBot] = useState<BotConfig | null>(null);
  const [webhookInfo, setWebhookInfo] = useState<any>(null);
  const [form] = Form.useForm();
  const [webhookForm] = Form.useForm();
  const [switchModeForm] = Form.useForm();
  const admin = JSON.parse(localStorage.getItem('admin') || '{}');

  const fetchBots = async () => {
    setLoading(true);
    try {
      const response = await botAPI.getAll();
      setBots(response.data);
    } catch (error: any) {
      message.error('获取机器人列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBots();
  }, []);

  const handleAdd = async (values: any) => {
    try {
      await botAPI.create(values);
      message.success('机器人添加成功！正在启动...');
      setModalVisible(false);
      form.resetFields();
      fetchBots();
    } catch (error: any) {
      message.error(error.response?.data?.error || '添加失败');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await botAPI.delete(id);
      message.success('机器人已删除');
      fetchBots();
    } catch (error: any) {
      message.error(error.response?.data?.error || '删除失败');
    }
  };

  const handleRestart = async (id: number) => {
    try {
      await botAPI.restart(id);
      message.success('机器人正在重启...');
      setTimeout(fetchBots, 2000);
    } catch (error: any) {
      message.error(error.response?.data?.error || '重启失败');
    }
  };

  const handleStop = async (id: number) => {
    try {
      await botAPI.stop(id);
      message.success('机器人已停止');
      fetchBots();
    } catch (error: any) {
      message.error(error.response?.data?.error || '停止失败');
    }
  };

  const handleDeleteWebhook = async (id: number) => {
    try {
      await botAPI.deleteWebhook(id);
      message.success('Webhook 已清除，已切换回 Polling 模式');
      fetchBots();
    } catch (error: any) {
      message.error(error.response?.data?.error || '清除 Webhook 失败');
    }
  };

  const handleSetWebhook = async (values: any) => {
    if (!currentBot) return;

    try {
      await botAPI.setWebhook(currentBot.id, values.webhookUrl);
      message.success('Webhook 已设置成功！');
      setWebhookModalVisible(false);
      webhookForm.resetFields();
      fetchBots();
    } catch (error: any) {
      message.error(error.response?.data?.error || '设置 Webhook 失败');
    }
  };

  const handleViewWebhookInfo = async (bot: BotConfig) => {
    setCurrentBot(bot);
    try {
      const response = await botAPI.getWebhookInfo(bot.id);
      setWebhookInfo(response.data);
      setWebhookInfoModalVisible(true);
    } catch (error: any) {
      message.error(error.response?.data?.error || '获取 Webhook 信息失败');
    }
  };

  const openSetWebhookModal = (bot: BotConfig) => {
    setCurrentBot(bot);
    webhookForm.setFieldsValue({
      webhookUrl: bot.webhook_url || '',
    });
    setWebhookModalVisible(true);
  };

  const openSwitchModeModal = (bot: BotConfig) => {
    setCurrentBot(bot);
    switchModeForm.setFieldsValue({
      mode: bot.polling_mode === 0 ? 'webhook' : 'polling',
      webhookUrl: bot.webhook_url || window.location.origin,
    });
    setSwitchModeModalVisible(true);
  };

  const handleSwitchMode = async (values: any) => {
    if (!currentBot) return;

    // 切换到 Webhook 模式时，检查是否有 webhook_url
    if (values.mode === 'webhook' && !values.webhookUrl) {
      message.error('切换到 Webhook 模式需要设置 Webhook URL');
      return;
    }

    try {
      const response = await botAPI.switchMode(currentBot.id, {
        mode: values.mode,
        webhookUrl: values.mode === 'webhook' ? values.webhookUrl : undefined,
      });
      message.success(response.data.message);
      setSwitchModeModalVisible(false);
      switchModeForm.resetFields();
      fetchBots();
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || '切换模式失败';
      const errorDetail = error.response?.data?.detail;

      if (errorDetail) {
        message.error(`${errorMsg}：${errorDetail}`);
      } else {
        message.error(errorMsg);
      }
    }
  };

  const handleCleanInvalidWebhooks = async () => {
    Modal.confirm({
      title: '清理无效 Webhook',
      content: (
        <div>
          <p>此操作将执行以下清理：</p>
          <ul>
            <li><strong>Polling 模式机器人</strong>的残留 Webhook</li>
            <li><strong>已删除机器人</strong>的孤立 Webhook（如果有）</li>
          </ul>
          <p>✅ 不会影响当前正在使用 Webhook 模式的机器人。</p>
          <p>⚠️ 待处理的消息更新将被丢弃，确定继续吗？</p>
        </div>
      ),
      okText: '确定清理',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const response = await botAPI.cleanInvalidWebhooks();
          const results = response.data.results || [];
          const cleaned = results.filter((r: any) => r.cleaned);

          if (cleaned.length > 0) {
            Modal.success({
              title: '清理成功',
              content: (
                <div>
                  <p>已清理 <strong>{cleaned.length}</strong> 个无效 Webhook：</p>
                  <ul style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {cleaned.map((item: any, index: number) => (
                      <li key={`${item.bot_id || 'orphaned'}-${index}`}>
                        {item.source === 'orphaned' && (
                          <Tag color="orange" style={{ marginRight: 8 }}>已删除</Tag>
                        )}
                        {item.bot_name}
                        {item.bot_id && ` (ID: ${item.bot_id})`}
                        <br />
                        <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                          {item.previous_url}
                        </Typography.Text>
                        {item.pending_updates > 0 && (
                          <>
                            <br />
                            <Typography.Text type="warning" style={{ fontSize: '12px' }}>
                              丢弃了 {item.pending_updates} 条待处理消息
                            </Typography.Text>
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                  <Typography.Text type="secondary" style={{ fontSize: '12px', marginTop: 16, display: 'block' }}>
                    检查总数: {response.data.total_checked} | 清理成功: {response.data.cleaned_count}
                  </Typography.Text>
                </div>
              ),
              width: 700,
            });
          } else {
            message.info('没有找到需要清理的无效 Webhook');
          }

          fetchBots();
        } catch (error: any) {
          message.error(error.response?.data?.error || '清理失败');
        }
      },
    });
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 60,
    },
    {
      title: '机器人名称',
      dataIndex: 'bot_name',
      width: 150,
    },
    {
      title: '用户名',
      dataIndex: 'bot_username',
      width: 150,
      render: (username: string) => username ? `@${username}` : '-',
    },
    {
      title: '运行状态',
      dataIndex: 'is_running',
      width: 120,
      render: (isRunning: boolean, record: BotConfig) => {
        if (record.start_error) {
          const errorMsg = record.start_error_code === 'INVALID_TOKEN'
            ? 'Token 无效，请更新或删除此机器人'
            : `启动失败: ${record.start_error}`;
          return (
            <Tooltip title={errorMsg}>
              <Tag icon={<WarningOutlined />} color="error" style={{ cursor: 'pointer' }}>
                Token错误
              </Tag>
            </Tooltip>
          );
        }
        return isRunning ? (
          <Tag icon={<CheckCircleOutlined />} color="success">
            运行中
          </Tag>
        ) : (
          <Tag icon={<CloseCircleOutlined />} color="default">
            已停止
          </Tag>
        );
      },
    },
    {
      title: '运行模式',
      dataIndex: 'polling_mode',
      width: 120,
      render: (pollingMode: number, record: BotConfig) => (
        <Space direction="vertical" size={0}>
          <Tag color={pollingMode === 0 ? 'green' : 'blue'}>
            {pollingMode === 0 ? 'Webhook' : 'Polling'}
          </Tag>
          {pollingMode === 0 && record.webhook_url && (
            <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
              {new URL(record.webhook_url).hostname}
            </Typography.Text>
          )}
        </Space>
      ),
    },
    {
      title: '类型',
      dataIndex: 'bot_type',
      width: 120,
      render: (botType: BotType) =>
        botType === 'group_management' ? (
          <Tag color="purple">群管/流量</Tag>
        ) : (
          <Tag color="cyan">记账</Tag>
        ),
    },
    {
      title: '描述',
      dataIndex: 'description',
      ellipsis: true,
      render: (desc: string) => desc || '-',
    },
    {
      title: '创建时间',
      dataIndex: 'create_time',
      width: 180,
      render: (time: string) => new Date(time).toLocaleString('zh-CN'),
    },
    {
      title: '操作',
      width: 480,
      render: (_: any, record: BotConfig) => (
        <Space wrap size="small">
          <Button
            size="small"
            icon={<SwapOutlined />}
            onClick={() => openSwitchModeModal(record)}
            disabled={admin.permission_level !== 2 || !record.is_running}
            type="primary"
          >
            切换模式
          </Button>
          <Button
            size="small"
            icon={<MessageOutlined />}
            onClick={() => navigate(`/bot-messages/${record.id}`)}
          >
            查看消息
          </Button>
          <Button
            size="small"
            icon={<InfoCircleOutlined />}
            onClick={() => handleViewWebhookInfo(record)}
            disabled={!record.is_running}
          >
            Webhook信息
          </Button>
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={() => handleRestart(record.id)}
            disabled={admin.permission_level !== 2}
          >
            重启
          </Button>
          <Button
            size="small"
            icon={<StopOutlined />}
            onClick={() => handleStop(record.id)}
            disabled={admin.permission_level !== 2 || !record.is_running}
          >
            停止
          </Button>
          <Popconfirm
            title="清除 Webhook 会切换回 Polling 模式并丢弃待处理的更新，确定继续吗？"
            onConfirm={() => handleDeleteWebhook(record.id)}
            okText="确定"
            cancelText="取消"
            disabled={admin.permission_level !== 2 || !record.is_running || record.polling_mode !== 0}
          >
            <Button
              size="small"
              icon={<DisconnectOutlined />}
              disabled={admin.permission_level !== 2 || !record.is_running || record.polling_mode !== 0}
              danger
            >
              清除 Webhook
            </Button>
          </Popconfirm>
          <Popconfirm
            title="确定删除此机器人吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
            disabled={admin.permission_level !== 2}
          >
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              disabled={admin.permission_level !== 2}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Title level={3}>机器人管理</Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchBots}>
            刷新
          </Button>
          {admin.permission_level === 2 && (
            <>
              <Button
                icon={<ClearOutlined />}
                onClick={handleCleanInvalidWebhooks}
                danger
              >
                清理无效 Webhook
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setModalVisible(true)}
              >
                添加机器人
              </Button>
            </>
          )}
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={bots}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title="添加机器人"
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAdd}
          initialValues={{ pollingMode: 1, botType: 'accounting' }}
        >
          <Form.Item
            label="Bot Token"
            name="botToken"
            rules={[{ required: true, message: '请输入 Bot Token' }]}
          >
            <Input placeholder="123456:ABC-DEF..." />
          </Form.Item>

          <Form.Item
            label="机器人名称"
            name="botName"
            rules={[{ required: true, message: '请输入机器人名称' }]}
          >
            <Input placeholder="My Bot" />
          </Form.Item>

          <Form.Item
            label="机器人类型"
            name="botType"
            rules={[{ required: true, message: '请选择机器人类型' }]}
            extra="类型创建后可修改，但会影响可用命令集"
          >
            <Radio.Group>
              <Radio value="accounting">记账机器人（群组财务管理、汇率）</Radio>
              <Radio value="group_management">群管/流量分发（禁言、自动回复、链接分发）</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item label="描述" name="description">
            <TextArea rows={3} placeholder="机器人用途说明（可选）" />
          </Form.Item>

          <Form.Item label="运行模式" name="pollingMode">
            <Radio.Group>
              <Radio value={1}>Polling 模式（推荐，无需配置域名）</Radio>
              <Radio value={0}>Webhook 模式（需要 HTTPS 域名）</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.pollingMode !== currentValues.pollingMode
            }
          >
            {({ getFieldValue }) =>
              getFieldValue('pollingMode') === 0 ? (
                <Form.Item
                  label="Webhook URL（域名地址）"
                  name="webhookUrl"
                  rules={[
                    { required: true, message: '请输入 Webhook URL' },
                    {
                      pattern: /^https:\/\/.+/,
                      message: 'Webhook URL 必须使用 HTTPS 协议',
                    },
                  ]}
                >
                  <Input
                    placeholder={window.location.origin}
                    prefix={<ApiOutlined />}
                  />
                </Form.Item>
              ) : (
                <Alert
                  message="Polling 模式说明"
                  description="机器人将通过轮询方式获取消息更新，无需配置域名。如需使用 Webhook 模式，可后续在「切换模式」中设置。"
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )
            }
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                添加
              </Button>
              <Button onClick={() => setModalVisible(false)}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Webhook 设置弹窗 */}
      <Modal
        title={`设置 Webhook - ${currentBot?.bot_name}`}
        open={webhookModalVisible}
        onCancel={() => {
          setWebhookModalVisible(false);
          webhookForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Alert
          message="Webhook 配置说明"
          description={
            <div>
              <p>• 必须使用 <strong>HTTPS</strong> 协议（Telegram 要求）</p>
              <p>• 填写你的域名地址，例如：<code>https://your-domain.com</code></p>
              <p>• 系统会自动在域名后添加路径：<code>/webhook/bot{'<TOKEN>'}</code></p>
              <p>• 设置成功后，机器人将自动切换为 Webhook 模式</p>
              <p>• 当前配置：{currentBot?.webhook_url || '未配置'}</p>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form form={webhookForm} layout="vertical" onFinish={handleSetWebhook}>
          <Form.Item
            label="Webhook URL（域名地址）"
            name="webhookUrl"
            rules={[
              { required: true, message: '请输入 Webhook URL' },
              {
                pattern: /^https:\/\/.+/,
                message: 'Webhook URL 必须使用 HTTPS 协议',
              },
            ]}
          >
            <Input
              placeholder="https://your-domain.com"
              prefix={<ApiOutlined />}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                设置 Webhook
              </Button>
              <Button onClick={() => setWebhookModalVisible(false)}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Webhook 信息查看弹窗 */}
      <Modal
        title={`Webhook 信息 - ${currentBot?.bot_name}`}
        open={webhookInfoModalVisible}
        onCancel={() => {
          setWebhookInfoModalVisible(false);
          setWebhookInfo(null);
        }}
        footer={[
          <Button key="close" onClick={() => setWebhookInfoModalVisible(false)}>
            关闭
          </Button>,
        ]}
        width={700}
      >
        {webhookInfo && (
          <div>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="运行模式">
                <Tag color={webhookInfo.polling_mode === 0 ? 'green' : 'blue'}>
                  {webhookInfo.polling_mode === 0 ? 'Webhook 模式' : 'Polling 模式'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="配置的 URL">
                {webhookInfo.configured_url || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="实际 Webhook URL">
                {webhookInfo.url || '未设置'}
              </Descriptions.Item>
              <Descriptions.Item label="待处理更新数">
                {webhookInfo.pending_update_count || 0}
              </Descriptions.Item>
              <Descriptions.Item label="最后错误">
                {webhookInfo.last_error_message || '无'}
              </Descriptions.Item>
              <Descriptions.Item label="最后错误时间">
                {webhookInfo.last_error_date
                  ? new Date(webhookInfo.last_error_date * 1000).toLocaleString('zh-CN')
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="最大连接数">
                {webhookInfo.max_connections || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="IP 地址">
                {webhookInfo.ip_address || '-'}
              </Descriptions.Item>
            </Descriptions>

            {webhookInfo.last_error_message && (
              <Alert
                message="检测到错误"
                description={webhookInfo.last_error_message}
                type="error"
                showIcon
                style={{ marginTop: 16 }}
              />
            )}
          </div>
        )}
      </Modal>

      {/* 切换模式弹窗 */}
      <Modal
        title={`切换运行模式 - ${currentBot?.bot_name}`}
        open={switchModeModalVisible}
        onCancel={() => {
          setSwitchModeModalVisible(false);
          switchModeForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Alert
          message="提示"
          description="切换运行模式会重启机器人，请在业务低峰期操作。Webhook 模式需要配置 HTTPS 域名。"
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form form={switchModeForm} layout="vertical" onFinish={handleSwitchMode}>
          <Form.Item
            label="运行模式"
            name="mode"
            rules={[{ required: true, message: '请选择运行模式' }]}
          >
            <Radio.Group>
              <Radio.Button value="polling">Polling 模式</Radio.Button>
              <Radio.Button value="webhook">Webhook 模式</Radio.Button>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.mode !== currentValues.mode}
          >
            {({ getFieldValue }) =>
              getFieldValue('mode') === 'webhook' ? (
                <Form.Item
                  label="Webhook URL（域名地址）"
                  name="webhookUrl"
                  rules={[
                    { required: true, message: '请输入 Webhook URL' },
                    {
                      pattern: /^https:\/\/.+/,
                      message: 'Webhook URL 必须使用 HTTPS 协议',
                    },
                  ]}
                >
                  <Input
                    placeholder={window.location.origin}
                    prefix={<ApiOutlined />}
                  />
                </Form.Item>
              ) : (
                <Alert
                  message="Polling 模式"
                  description="机器人将通过轮询方式获取消息更新，无需配置域名，适合开发环境。"
                  type="info"
                  showIcon
                />
              )
            }
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                确认切换
              </Button>
              <Button onClick={() => setSwitchModeModalVisible(false)}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BotsManagement;
